<template>
  <div class="selectGrade" v-if="visible" @click.self="handleClose">
    <div class="grade-popup">
      <div class="grade-content">
        <img
          src="@/assets/aiTeacher/grade-close.png"
          alt=""
          class="grade-close-btn"
          @click="handleClose"
        />
        <div class="title">切换年级</div>

        <div class="grade-list">
          <div
            class="grade-item"
            v-for="item in gradeList"
            :key="item.id"
            :class="{ active: String(selectedGradeId) === String(item.id) }"
            @click="handleSelectGrade(item)"
          >
            {{ item.name }}
          </div>
        </div>
        <div class="btn" @click="handleConfirm">确 定</div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, computed, defineProps, defineEmits } from "vue";

const props = defineProps({
  visible: {
    type: Boolean,
    default: false,
  },
  currentGradeId: {
    type: String,
    default: "",
  },
  gradeList: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(["close", "confirm"]);

const selectedGradeId = ref("");

// ✅ 使用传入的年级列表
const gradeList = computed(() => props.gradeList || []);

// 监听 visible 变化，重置选中状态
watch(
  () => props.visible,
  (newVal) => {
    if (newVal) {
      // ✅ 设置默认选中值：使用 currentGradeId
      selectedGradeId.value = String(props.currentGradeId || "");
    }
  }
);

// 监听 currentGradeId 变化
watch(
  () => props.currentGradeId,
  (newVal) => {
    if (newVal) {
      selectedGradeId.value = String(newVal); // 确保转换为字符串，便于比较
    }
  },
  { immediate: true }
);

// ✅ 监听年级列表变化，设置默认选中值（当列表加载完成后）
watch(
  () => gradeList.value,
  (newList) => {
    if (newList && newList.length > 0) {
      // 如果当前没有选中值，或者选中的值不在列表中，则选中 currentGradeId 对应的值
      const currentIdStr = String(props.currentGradeId || "");
      if (!selectedGradeId.value || !newList.find(item => String(item.id) === selectedGradeId.value)) {
        if (props.currentGradeId) {
          // 查找 currentGradeId 对应的年级
          const matchedGrade = newList.find(item => String(item.id) === currentIdStr);
          if (matchedGrade) {
            selectedGradeId.value = String(matchedGrade.id);
            console.log("✅ [切换年级] 设置默认选中值:", matchedGrade);
          } else {
            // 如果找不到匹配的，默认选中第一个
            selectedGradeId.value = String(newList[0].id);
            console.log("⚠️ [切换年级] 未找到匹配的年级，默认选中第一个:", newList[0]);
          }
        } else {
          // 如果没有 currentGradeId，默认选中第一个
          selectedGradeId.value = String(newList[0].id);
          console.log("ℹ️ [切换年级] 没有 currentGradeId，默认选中第一个:", newList[0]);
        }
      }
    }
  },
  { immediate: true }
);

// 选择年级
const handleSelectGrade = (item) => {
  selectedGradeId.value = String(item.id); // 确保转换为字符串
  console.log("📝 [切换年级] 选中年级:", item);
};

// 确认选择
const handleConfirm = () => {
  const selectedGrade = gradeList.value.find(
    (item) => item.id === selectedGradeId.value
  );
  if (selectedGrade) {
    console.log("✅ [切换年级] 确认选择年级:", selectedGrade);
    emit("confirm", {
      gradeId: selectedGrade.id,
      gradeName: selectedGrade.name,
    });
  }
  handleClose();
};

// 关闭弹窗
const handleClose = () => {
  emit("close");
};
</script>

<style lang="scss">
.selectGrade {
  position: fixed;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.6);
  left: 0;
  top: 0;

  .grade-popup {
    display: flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    padding: 5px;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    background-color: #7e7c84;
    border-radius: 50px;

    .grade-content {
      display: flex;
      flex-direction: column;
      align-items: center;
      position: relative;
      width: 472px;
      min-height: 310px;
      background-color: #fff;
      border-radius: 50px;
      padding: 16px 36px 25px;
      box-sizing: border-box;
      .grade-close-btn {
        position: absolute;
        top: -16px;
        right: -16px;
        transform: translate(50%, 0%);
        width: 32px;
        height: 32px;
      }

      .title {
        position: relative;
        font-weight: bold;
        font-size: 18px;
        color: #333333;

        &::before {
          position: absolute;
          left: -6px;
          top: 50%;
          transform: translate(-100%, -50%);
          content: "";
          display: block;
          width: 16px;
          height: 15px;
          background: url("@/assets/aiTeacher/tit-left.png") no-repeat center
            center;
          background-size: cover;
        }
        &::after {
          position: absolute;
          right: -6px;
          top: 50%;
          transform: translate(100%, -50%);
          content: "";
          display: block;
          width: 16px;
          height: 15px;
          background: url("@/assets/aiTeacher/tit-right.png") no-repeat center
            center;
          background-size: cover;
        }
      }
      .grade-list {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        margin: 30px 0;
        .grade-item {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 120px;
          height: 38px;
          background: #f4f6ff;
          border-radius: 20px;
          color: #7F8A9D;
          font-size: 14px;
          margin-bottom: 15px;
          margin-right: 20px;
          cursor: pointer;
          transition: all 0.3s ease;
          &:nth-child(3n) {
            margin-right: 0;
          }
          &.active {
            background: #FFFAF2;
            border: 1px solid #FFA061;
            color: #FFA061;
          }
        }
      }
      .btn {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 260px;
        height: 40px;
        background: linear-gradient(90deg, #ffae79 0%, #ffa061 100%);
        box-shadow: inset 0px 1px 3px 1px rgba(255, 255, 255, 0.3);
        border-radius: 20px;
        font-size: 15px;
        color: #ffffff;
      }
    }
  }
}
</style>
